
import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUserProfile, useUpdateUserProfile } from '@/hooks/useUserProfile';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useIsAdmin } from '@/hooks/useUserRole';
import { User, Mail, Phone, Building2, Pencil, Save } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { OrganizationLogoUploader } from './OrganizationLogoUploader';

const UserProfileSettings = () => {
  const { data: profile, isLoading } = useUserProfile();
  const { currentOrganization } = useOrganization();
  const isAdmin = useIsAdmin();
  const updateProfile = useUpdateUserProfile();

  const organizationName = profile?.organization_name || currentOrganization?.name || 'Organization name not available';

  const [formData, setFormData] = useState({
    name: '',
    phone_number: '',
  });

  const nameInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || '',
        phone_number: profile.phone_number || '',
      });
    }
  }, [profile]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile.mutate(formData);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12 bg-white rounded-[24px] border border-gray-100 shadow-sm">
        <LoadingSpinner size="medium" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[24px] border border-gray-200/70 p-6 md:p-12 shadow-sm">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header section */}
        <div className="flex flex-col sm:flex-row items-start justify-between gap-4 pb-6 border-b border-gray-100">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#F5EEFE] flex items-center justify-center shrink-0">
              <User className="w-6 h-6 text-[#9333EA]" />
            </div>
            <div className="space-y-1">
              <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                User Profile
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 font-normal leading-relaxed">
                Update your personal information and account details.
              </p>
            </div>
          </div>

          {/* Top-Right Decorative Illustration Badge */}
          <div className="relative shrink-0 self-end sm:self-auto">
            <div className="w-44 h-24 rounded-2xl bg-[#F5EEFE]/80 p-3.5 flex flex-col justify-center border border-purple-100/60 shadow-sm">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm text-[#9333EA] shrink-0">
                  <User className="w-5 h-5 text-[#9333EA]" />
                </div>
                <div className="space-y-1.5 flex-1">
                  <div className="w-14 h-2 bg-purple-200/70 rounded-full" />
                  <div className="w-10 h-2 bg-purple-200/70 rounded-full" />
                </div>
              </div>
              <div className="w-20 h-2 bg-purple-200/70 rounded-full mt-2.5 ml-1" />
            </div>
            <div className="w-7 h-7 rounded-full bg-[#9333EA] text-white flex items-center justify-center shadow-md absolute -bottom-1 -right-1 border-2 border-white">
              <Pencil className="w-3.5 h-3.5 text-white" />
            </div>
          </div>
        </div>

        {/* Form Fields Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 py-2">
          {/* Name Field (Editable) */}
          <div className="space-y-1.5 max-w-[520px]">
            <Label htmlFor="name" className="text-xs sm:text-sm font-semibold text-gray-900">
              Name
            </Label>
            <div className="flex items-center rounded-xl border border-gray-200 focus-within:border-[#9333EA] focus-within:ring-2 focus-within:ring-purple-100 overflow-hidden bg-white transition-all">
              <div className="w-12 h-11 bg-[#F5EEFE] flex items-center justify-center shrink-0 border-r border-purple-100">
                <User className="w-5 h-5 text-[#9333EA]" />
              </div>
              <Input
                id="name"
                ref={nameInputRef}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter your full name"
                className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-11 px-3.5 text-sm text-gray-900 placeholder:text-gray-400 bg-transparent w-full"
              />
              <button
                type="button"
                onClick={() => nameInputRef.current?.focus()}
                className="px-3.5 text-purple-600 hover:text-purple-700 transition-colors shrink-0"
                title="Edit Name"
              >
                <Pencil className="w-4 h-4 text-[#9333EA]" />
              </button>
            </div>
          </div>

          {/* Email Field (Read-only / Disabled) */}
          <div className="space-y-1.5 max-w-[520px]">
            <Label htmlFor="email" className="text-xs sm:text-sm font-semibold text-gray-900">
              Email
            </Label>
            <div className="flex items-center rounded-xl border border-gray-200/80 overflow-hidden bg-[#FAFAFA]">
              <div className="w-12 h-11 bg-[#F5EEFE] flex items-center justify-center shrink-0 border-r border-purple-100/60 opacity-80">
                <Mail className="w-5 h-5 text-[#9333EA]" />
              </div>
              <Input
                id="email"
                value={profile?.email || ''}
                disabled
                readOnly
                className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-11 px-3.5 text-sm text-gray-500 bg-transparent w-full cursor-not-allowed"
              />
            </div>
            <p className="text-xs text-gray-500">
              Email cannot be changed here. Contact support if needed.
            </p>
          </div>

          {/* Phone Number Field (Editable) */}
          <div className="space-y-1.5 max-w-[520px]">
            <Label htmlFor="phone" className="text-xs sm:text-sm font-semibold text-gray-900">
              Phone Number
            </Label>
            <div className="flex items-center rounded-xl border border-gray-200 focus-within:border-[#9333EA] focus-within:ring-2 focus-within:ring-purple-100 overflow-hidden bg-white transition-all">
              <div className="w-12 h-11 bg-[#F5EEFE] flex items-center justify-center shrink-0 border-r border-purple-100">
                <Phone className="w-5 h-5 text-[#9333EA]" />
              </div>
              <Input
                id="phone"
                ref={phoneInputRef}
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                placeholder="Enter your phone number"
                className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-11 px-3.5 text-sm text-gray-900 placeholder:text-gray-400 bg-transparent w-full"
              />
              <button
                type="button"
                onClick={() => phoneInputRef.current?.focus()}
                className="px-3 text-purple-600 hover:text-purple-700 transition-colors shrink-0"
                title="Edit Phone Number"
              >
                <Pencil className="w-4 h-4 text-[#9333EA]" />
              </button>
            </div>
          </div>

          {/* Organization Logo Field (Right column, directly below Email) */}
          <OrganizationLogoUploader compact />

          {/* Organization Field (Full width below Phone Number) */}
          <div className="space-y-1.5 col-span-1 md:col-span-2 max-w-[520px]">
            <Label htmlFor="organization" className="text-xs sm:text-sm font-semibold text-gray-900">
              Organization
            </Label>
            <div className="flex items-center rounded-xl border border-gray-200/80 overflow-hidden bg-[#FAFAFA]">
              <div className="w-12 h-11 bg-[#F5EEFE] flex items-center justify-center shrink-0 border-r border-purple-100/60 opacity-80">
                <Building2 className="w-5 h-5 text-[#9333EA]" />
              </div>
              <Input
                id="organization"
                value={organizationName}
                disabled
                readOnly
                className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-11 px-3.5 text-sm text-gray-500 bg-transparent w-full cursor-not-allowed"
              />
            </div>
            <p className="text-xs text-gray-500">
              You can view and manage organization details from your organization settings.
            </p>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end pt-4 border-t border-gray-100">
          <Button
            type="submit"
            variant="default"
            disabled={updateProfile.isPending}
            className="rounded-xl px-6 flex items-center gap-2"
          >
            {updateProfile.isPending ? (
              <LoadingSpinner size="small" iconClassName="text-white" label="Saving..." />
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default UserProfileSettings;

